import { ApplicationConfig, APP_INITIALIZER, inject, importProvidersFrom } from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideToastr } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { MqttModule, IMqttServiceOptions } from 'ngx-mqtt';

import { routes } from './app.routes';
import { authInterceptor, errorInterceptor } from './core';
import { environment } from '../environments/environment';


export const MQTT_SERVICE_OPTIONS: IMqttServiceOptions = {
  hostname: environment.mqtt.hostname,
  port: environment.mqtt.port,
  protocol: environment.mqtt.protocol,
  path: environment.mqtt.path,
  clean: true, // Clean session - don't persist subscriptions
  connectOnCreate: true, // Connect immediately when service is created
  reconnectPeriod: 3000, // Reconnect every 3 seconds if disconnected
  connectTimeout: 10000, // 10 seconds connection timeout
};

export function initializeApp(translate: TranslateService): () => Promise<void> {
  return async () => {
    const storedLang = localStorage.getItem('LOCALIZE_DEFAULT_LANGUAGE') || 'en';
    translate.setDefaultLang(storedLang);
    await firstValueFrom(translate.use(storedLang));

    // Set document direction based on language
    const dir = storedLang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', storedLang);
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withComponentInputBinding()),
    provideHttpClient(withInterceptors([authInterceptor, errorInterceptor])),
    provideAnimations(),
    importProvidersFrom(MqttModule.forRoot(MQTT_SERVICE_OPTIONS)),
    provideToastr({
      timeOut: 4000,
      positionClass: 'toast-top-right',
      preventDuplicates: true,
      progressBar: true,
      closeButton: true,
      newestOnTop: true,
    }),
    provideTranslateService({
      defaultLanguage: 'en',
    }),
    provideTranslateHttpLoader({
      prefix: './assets/i18n/',
      suffix: '.json',
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const translate = inject(TranslateService);
        return initializeApp(translate);
      },
      multi: true,
    },
  ],
};
